import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { 
  Apple, Flame, Target, ChevronLeft, ChevronRight, 
  PieChart, Footprints, Plus, Beef, Wheat, Droplet,
  Trash2, Copy, Mic, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { useSync } from '../contexts/SyncContext';
import { SyncIndicator } from './SyncIndicator';
import { trackUsage, FEATURES, NUTRITION_SUB_FEATURES } from '../services/usageTracking';
import { NutritionSkeleton } from './Skeletons';
import api from '../services/api';


// Import refactored components and modals
import {
  NutritionProvider,
  useNutrition,
  ActionButtons,
  MacrosSummary,
  NutritionHeader,
  FoodEntryList,
  BarcodeScannerModal,
  FoodSearchModal,
  EditGoalsModal,
  SavedMealsModal,
  FreshProduceModal,
  CustomProductsModal,
  SavedBarcodesModal,
  ManualEntryModal,
  FoodPhotoModal,
  CopyFoodModal,
  AddMealTimeModal,
  MenuScannerModal,
  DEFAULT_MEAL_TIMES,
  NUTRITION_KEY,
  stepsToCalories,
} from './nutrition';
import { useVoiceFood } from './nutrition/hooks/useVoiceFood';
import WalkImpactCard from './nutrition/components/WalkImpactCard';

// Ring/Circle Progress component for macros display
function RingProgress({ 
  value, 
  max, 
  size = 100, 
  strokeWidth = 8, 
  color = '#22c55e',
  bgColor = 'rgba(100, 116, 139, 0.3)',
  label,
  sublabel 
}) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const percent = Math.min((value / max) * 100, 100);
  const strokeDashoffset = circumference - (percent / 100) * circumference;
  
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="transform -rotate-90"
      >
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={bgColor}
          strokeWidth={strokeWidth}
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          style={{ transition: 'stroke-dashoffset 0.5s ease-out' }}
        />
      </svg>
      {/* Center text */}
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-white font-bold" style={{ fontSize: size * 0.2 }}>{label}</span>
        {sublabel && (
          <span className="text-slate-400" style={{ fontSize: size * 0.11 }}>{sublabel}</span>
        )}
      </div>
    </div>
  );
}

// Dual Ring Progress - Inner ring for consumed, outer ring for burned
function DualRingProgress({ 
  consumedValue,
  burnedValue,
  max,
  size = 120,
  label,
  sublabel
}) {
  const outerStrokeWidth = 8;
  const innerStrokeWidth = 10;
  const gap = 4;
  
  const outerRadius = (size - outerStrokeWidth) / 2;
  const innerRadius = outerRadius - outerStrokeWidth - gap;
  
  const outerCircumference = 2 * Math.PI * outerRadius;
  const innerCircumference = 2 * Math.PI * innerRadius;
  
  const burnedPercent = Math.min((burnedValue / max) * 100, 100);
  const consumedPercent = Math.min((consumedValue / max) * 100, 100);
  
  const outerOffset = outerCircumference - (burnedPercent / 100) * outerCircumference;
  const innerOffset = innerCircumference - (consumedPercent / 100) * innerCircumference;
  
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="transform -rotate-90"
      >
        {/* Outer ring background (Burned) */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={outerRadius}
          fill="none"
          stroke="rgba(251, 146, 60, 0.2)"
          strokeWidth={outerStrokeWidth}
        />
        {/* Outer ring progress (Burned - Orange) */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={outerRadius}
          fill="none"
          stroke="#f97316"
          strokeWidth={outerStrokeWidth}
          strokeLinecap="round"
          strokeDasharray={outerCircumference}
          strokeDashoffset={outerOffset}
          style={{ transition: 'stroke-dashoffset 0.5s ease-out' }}
        />
        
        {/* Inner ring background (Consumed) */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={innerRadius}
          fill="none"
          stroke="rgba(34, 197, 94, 0.2)"
          strokeWidth={innerStrokeWidth}
        />
        {/* Inner ring progress (Consumed - Green) */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={innerRadius}
          fill="none"
          stroke={consumedValue > max ? '#ef4444' : '#22c55e'}
          strokeWidth={innerStrokeWidth}
          strokeLinecap="round"
          strokeDasharray={innerCircumference}
          strokeDashoffset={innerOffset}
          style={{ transition: 'stroke-dashoffset 0.5s ease-out' }}
        />
      </svg>
      {/* Center text */}
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-white font-bold" style={{ fontSize: size * 0.18 }}>{label}</span>
        {sublabel && (
          <span className="text-slate-400" style={{ fontSize: size * 0.1 }}>{sublabel}</span>
        )}
      </div>
    </div>
  );
}

// Inner component that uses the context
function NutritionTrackerInner({ onBack, isExpired, onNavigate }) {
  const {
    selectedDate,
    entries,
    goals,
    setGoals,
    burnedCalories,
    externalSteps,
    bodyWeight,
    bmrData,
    isLoading,
    allMealTimes,
    selectedMealTime,
    setSelectedMealTime,
    savedBarcodes,
    saveBarcode,
    deleteBarcode,
    updateBarcode,
    savedMeals,
    saveMeal,
    deleteMeal,
    renameMeal,
    customProducts,
    saveCustomProduct,
    savedMenus,
    addEntry,
    removeEntry,
    changeDate,
    goToToday,
    updateBurnedCalories,
    updateExternalSteps,
    burnedCaloriesOverride,
    addCustomMealTime,
    syncNutritionToBackend,
  } = useNutrition();

  // Modal states
  const [showEditGoals, setShowEditGoals] = useState(false);
  const [showFoodSearch, setShowFoodSearch] = useState(false);
  const [showBarcodeScanner, setShowBarcodeScanner] = useState(false);
  const [showSavedBarcodes, setShowSavedBarcodes] = useState(false);
  const [showSavedMeals, setShowSavedMeals] = useState(false);
  const [showFreshProduce, setShowFreshProduce] = useState(false);
  const [showCustomProducts, setShowCustomProducts] = useState(false);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [showFoodPhoto, setShowFoodPhoto] = useState(false);
  const [showCopyFood, setShowCopyFood] = useState(false);
  const [showAddMealTime, setShowAddMealTime] = useState(false);
  const [showMenuScanner, setShowMenuScanner] = useState(false);
  
  // Copy modal state
  const [copyEntry, setCopyEntry] = useState(null);

  // Voice food logging
  const voiceFood = useVoiceFood({ addEntry });

  // Blood sugar data for mealtime sync
  const [bloodSugarByMeal, setBloodSugarByMeal] = useState({});
  const bloodSugarRef = useRef({});
  const diabetesEnabled = localStorage.getItem('diabetes_enabled') === 'true';
  const diabetesUnit = localStorage.getItem('diabetes_unit') || 'mmol/l';
  const insulinEnabled = localStorage.getItem('diabetes_insulin') === 'true';

  useEffect(() => { bloodSugarRef.current = bloodSugarByMeal; }, [bloodSugarByMeal]);

  const fetchBloodSugar = useCallback(async () => {
    if (!diabetesEnabled) { setBloodSugarByMeal({}); return; }
    try {
      const data = await api.get(`/diabetes/by-mealtime?date=${selectedDate}`);
      setBloodSugarByMeal(data?.by_mealtime || {});
    } catch { setBloodSugarByMeal({}); }
  }, [selectedDate, diabetesEnabled]);

  useEffect(() => {
    fetchBloodSugar();
  }, [fetchBloodSugar]);

  // Handle blood sugar change from mealtime input (supports before/after timing)
  const handleBloodSugarChange = useCallback(async (mealId, action, value, existingEntry, timing = 'before') => {
    if (action === 'value' || action === 'insulin') {
      setBloodSugarByMeal(prev => {
        const updated = { ...prev };
        const entries = [...(updated[mealId] || [])];
        let entryIdx = entries.findIndex(e => e.timing === timing);
        if (entryIdx === -1) {
          entries.push({ value: null, unit: diabetesUnit, timing, insulin_short: null });
          entryIdx = entries.length - 1;
        }
        const current = { ...entries[entryIdx] };
        if (action === 'value') current.value = value;
        if (action === 'insulin') current.insulin_short = value;
        entries[entryIdx] = current;
        updated[mealId] = entries;
        return updated;
      });
    } else if (action === 'save' || action === 'save_insulin') {
      try {
        const entries = bloodSugarRef.current[mealId] || [];
        const existing = entries.find(e => e.timing === timing);
        if (existing?.entry_id) {
          const updateData = action === 'save' ? { value } : { insulin_short: value };
          await api.put(`/diabetes/${existing.entry_id}`, updateData);
        } else {
          const payload = {
            value: action === 'save' ? value : 0,
            unit: diabetesUnit,
            mealtime: mealId,
            timing,
            date: selectedDate,
          };
          if (action === 'save_insulin') {
            payload.insulin_short = value;
            payload.value = existing?.value || 0;
          }
          await api.post('/diabetes', payload);
        }
        fetchBloodSugar();
      } catch (err) {
        console.error('Failed to save blood sugar:', err);
      }
    } else if (action === 'delete_value') {
      try {
        const entries = bloodSugarRef.current[mealId] || [];
        const existing = entries.find(e => e.timing === timing);
        if (existing?.entry_id) {
          if (!existing.insulin_short) {
            await api.delete(`/diabetes/${existing.entry_id}`);
          } else {
            await api.put(`/diabetes/${existing.entry_id}`, { value: 0 });
          }
          fetchBloodSugar();
        }
      } catch (err) {
        console.error('Failed to delete blood sugar value:', err);
      }
    } else if (action === 'delete_insulin') {
      try {
        const entries = bloodSugarRef.current[mealId] || [];
        const existing = entries.find(e => e.timing === timing);
        if (existing?.entry_id) {
          if (!existing.value || existing.value === 0) {
            await api.delete(`/diabetes/${existing.entry_id}`);
          } else {
            await api.put(`/diabetes/${existing.entry_id}`, { insulin_short: 0 });
          }
          fetchBloodSugar();
        }
      } catch (err) {
        console.error('Failed to delete insulin value:', err);
      }
    }
  }, [diabetesUnit, selectedDate, fetchBloodSugar]);

  // Swipe handling for mobile date navigation
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);
  const minSwipeDistance = 100;

  const handleTouchStart = (e) => {
    touchEndX.current = null;
    touchStartX.current = e.targetTouches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;
    
    const distance = touchStartX.current - touchEndX.current;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe) {
      const today = new Date().toISOString().split('T')[0];
      if (selectedDate < today) {
        changeDate(1);
      }
    } else if (isRightSwipe) {
      changeDate(-1);
    }
    
    touchStartX.current = null;
    touchEndX.current = null;
  };

  // Track usage
  useEffect(() => {
    trackUsage(FEATURES.NUTRITION_TRACKER);
  }, []);

  useEffect(() => {
    if (showFoodSearch) trackUsage(FEATURES.NUTRITION_TRACKER, NUTRITION_SUB_FEATURES.DATABASE);
  }, [showFoodSearch]);

  useEffect(() => {
    if (showFreshProduce) trackUsage(FEATURES.NUTRITION_TRACKER, NUTRITION_SUB_FEATURES.FRESH_PRODUCE);
  }, [showFreshProduce]);

  useEffect(() => {
    if (showBarcodeScanner) trackUsage(FEATURES.NUTRITION_TRACKER, NUTRITION_SUB_FEATURES.SCAN);
  }, [showBarcodeScanner]);

  useEffect(() => {
    if (showSavedBarcodes) trackUsage(FEATURES.NUTRITION_TRACKER, NUTRITION_SUB_FEATURES.MY_SCANNED);
  }, [showSavedBarcodes]);

  useEffect(() => {
    if (showSavedMeals) trackUsage(FEATURES.NUTRITION_TRACKER, NUTRITION_SUB_FEATURES.MY_MEALS);
  }, [showSavedMeals]);

  useEffect(() => {
    if (showCustomProducts) trackUsage(FEATURES.NUTRITION_TRACKER, NUTRITION_SUB_FEATURES.MY_PRODUCTS);
  }, [showCustomProducts]);

  useEffect(() => {
    if (showMenuScanner) trackUsage(FEATURES.NUTRITION_TRACKER, NUTRITION_SUB_FEATURES.RESTAURANT_MENU);
  }, [showMenuScanner]);

  useEffect(() => {
    if (showFoodPhoto) trackUsage(FEATURES.NUTRITION_TRACKER, NUTRITION_SUB_FEATURES.MEAL_PHOTO);
  }, [showFoodPhoto]);

  // Calculate totals
  const totals = entries.reduce(
    (acc, entry) => ({
      calories: acc.calories + (entry.calories || 0),
      protein: acc.protein + (entry.protein || 0),
      carbs: acc.carbs + (entry.carbs || 0),
      fat: acc.fat + (entry.fat || 0),
    }),
    { calories: 0, protein: 0, carbs: 0, fat: 0 }
  );

  const getPercentage = (current, goal) => {
    if (!goal || goal === 0) return 0;
    return Math.min(Math.round((current / goal) * 100), 100);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const today = new Date().toISOString().split('T')[0];
    
    if (dateStr === today) return 'Today';
    
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    if (dateStr === yesterday.toISOString().split('T')[0]) return 'Yesterday';
    
    return `${dayNames[date.getDay()]}, ${monthNames[date.getMonth()]} ${date.getDate()}`;
  };

  // Handle action button clicks
  const handleAction = (actionId) => {
    if (isExpired) {
      toast.error('Your trial has expired. Please subscribe to continue.');
      return;
    }

    switch (actionId) {
      case 'database':
        setShowFoodSearch(true);
        break;
      case 'fresh':
        setShowFreshProduce(true);
        break;
      case 'products':
        setShowCustomProducts(true);
        break;
      case 'meals':
        setShowSavedMeals(true);
        break;
      case 'scanned':
        setShowSavedBarcodes(true);
        break;
      case 'scan':
        setShowBarcodeScanner(true);
        break;
      case 'restaurant':
        setShowMenuScanner(true);
        break;
      case 'photo':
        setShowFoodPhoto(true);
        break;
      default:
        break;
    }
  };

  // Handle adding food from modals
  const handleAddFood = (food) => {
    // Use the food's mealTime if provided, otherwise use selectedMealTime
    const mealTime = food.mealTime || selectedMealTime;
    addEntry({
      ...food,
      mealTime,
    });
    // If food came with a different mealTime, optionally switch to that view
    if (food.mealTime && food.mealTime !== selectedMealTime) {
      setSelectedMealTime(food.mealTime);
    }
  };

  // Handle copy food entry
  const handleCopyEntry = (entry) => {
    setCopyEntry(entry);
    setShowCopyFood(true);
  };

  // Handle copy to date
  const handleCopyToDate = (entry, targetDate, targetMealTime) => {
    // Check if this is a "copy all" operation
    const entriesToCopy = entry._copyAll && entry._allEntries 
      ? entry._allEntries 
      : [entry];
    
    if (targetDate === selectedDate) {
      // Copying to the same date
      entriesToCopy.forEach((e, i) => {
        addEntry({
          ...e,
          id: undefined, // Let addEntry generate a unique ID
          mealTime: targetMealTime,
          _copyAll: undefined,
          _allEntries: undefined,
          _mealName: undefined,
        });
      });
      toast.success(`Copied ${entriesToCopy.length} item${entriesToCopy.length > 1 ? 's' : ''} to ${targetMealTime}`);
    } else {
      // Copying to a different date
      const savedNutrition = JSON.parse(localStorage.getItem(NUTRITION_KEY) || '{}');
      const targetEntries = savedNutrition[targetDate] || [];
      
      entriesToCopy.forEach((e, i) => {
        targetEntries.push({
          ...e,
          id: `entry_${Date.now()}_${i}_${Math.random().toString(36).slice(2, 9)}`,
          mealTime: targetMealTime,
          _copyAll: undefined,
          _allEntries: undefined,
          _mealName: undefined,
        });
      });
      
      savedNutrition[targetDate] = targetEntries;
      localStorage.setItem(NUTRITION_KEY, JSON.stringify(savedNutrition));
      syncNutritionToBackend(targetDate, targetEntries);
      
      const dateObj = new Date(targetDate + 'T12:00:00');
      const dateLabel = targetDate === new Date().toISOString().split('T')[0] ? 'Today' : dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      toast.success(`Copied ${entriesToCopy.length} item${entriesToCopy.length > 1 ? 's' : ''} to ${dateLabel}`);
    }
  };

  // Show skeleton while loading
  if (isLoading) {
    return <NutritionSkeleton />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-slate-950 border-none ios-header" style={{ paddingTop: 'max(16px, env(safe-area-inset-top, 16px))', border: 'none' }}>
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg shadow-amber-500/20">
              <Apple className="h-5 w-5 text-white" />
            </div>
            <h1 className="text-lg font-heading font-bold tracking-tight text-white">
              Nutrition
            </h1>
            <SyncIndicator compact={true} showLabel={false} />
          </div>
          <div className="flex items-center gap-1.5">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowEditGoals(true)}
              className="text-slate-400 hover:text-amber-400"
            >
              <Target className="h-4 w-4 mr-1" />
              Goals
            </Button>
          </div>
        </div>
      </header>

      <main 
        className="container mx-auto px-4 py-6 pb-36"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div className="max-w-lg mx-auto space-y-4">
          {/* Date Navigation */}
          <Card className="bg-slate-900/80 border-slate-800 rounded-2xl" data-testid="date-nav">
            <CardContent className="p-3">
              <div className="flex items-center justify-between">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => changeDate(-1)}
                  className="text-slate-400 hover:text-white hover:bg-slate-800 rounded-xl"
                >
                  <ChevronLeft className="h-5 w-5" />
                </Button>
                
                <div className="text-center">
                  <p className="text-lg font-heading font-bold text-white">{formatDate(selectedDate)}</p>
                  <p className="text-xs text-slate-500">{selectedDate}</p>
                </div>
                
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => changeDate(1)}
                  disabled={selectedDate >= new Date().toISOString().split('T')[0]}
                  className="text-slate-400 hover:text-white disabled:opacity-30"
                >
                  <ChevronRight className="h-5 w-5" />
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Calories Summary with Ring Diagram */}
          <Card className="bg-slate-900/80 border-slate-800 rounded-2xl" data-testid="daily-summary">
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                {/* Left side - Stats */}
                <div className="space-y-2 flex-1">
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full bg-orange-500"></div>
                    <span className="text-slate-400 text-sm">Burned</span>
                    <span className="text-green-400 text-sm ml-auto">+{burnedCalories + stepsToCalories(externalSteps, bodyWeight)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full bg-green-500"></div>
                    <span className="text-slate-400 text-sm">Consumed</span>
                    <span className="text-white text-sm font-semibold ml-auto">{Math.round(totals.calories)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full bg-green-400"></div>
                    <span className="text-slate-400 text-sm">Allowance</span>
                    <span className="text-cyan-400 text-sm ml-auto">{bmrData?.dailyTarget || goals.calories}</span>
                  </div>
                </div>
                
                {/* Right side - Dual Ring Diagram (outer=burned, inner=consumed) */}
                <div className="flex flex-col items-center ml-4">
                  <DualRingProgress
                    consumedValue={totals.calories}
                    burnedValue={burnedCalories + stepsToCalories(externalSteps, bodyWeight)}
                    max={bmrData?.dailyTarget || goals.calories}
                    size={110}
                    label={Math.round(totals.calories)}
                    sublabel={`/ ${bmrData?.dailyTarget || goals.calories}`}
                  />
                </div>
              </div>
              
              {/* Goal and Deficit Row */}
              <div className="grid grid-cols-2 gap-2 mt-3">
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-2.5 text-center">
                  <span className="text-slate-400 text-xs">Goal</span>
                  <p className={`font-bold text-sm ${bmrData.goal === 'lose' ? 'text-red-400' : 'text-green-400'}`}>
                    {bmrData.goal === 'lose' ? 'Lose Weight' : 'Maintain'}
                  </p>
                  {bmrData.goal === 'lose' && bmrData.cycleWeek > 0 && (
                    <>
                      <p className="text-[10px] text-slate-500 mt-0.5">
                        W{bmrData.cycleWeek}/20 {bmrData.cyclePhase} ({bmrData.calorieAdjustment})
                      </p>
                      <div className="mt-1.5 flex gap-[1px]" data-testid="cycle-progress-bar">
                        {Array.from({ length: 20 }, (_, i) => {
                          const week = i + 1;
                          const isCurrentWeek = week === bmrData.cycleWeek;
                          let color;
                          if (week <= 2) color = 'bg-orange-400';
                          else if (week <= 10) color = 'bg-red-400';
                          else if (week <= 12) color = 'bg-orange-400';
                          else color = 'bg-green-400';
                          return (
                            <div
                              key={week}
                              className={`h-1.5 flex-1 rounded-[1px] transition-all ${
                                isCurrentWeek
                                  ? `${color} ring-1 ring-white ring-offset-1 ring-offset-slate-800 h-2.5 -mt-0.5`
                                  : week < bmrData.cycleWeek ? `${color} opacity-80` : `${color} opacity-25`
                              }`}
                              title={`W${week}: ${week <= 2 ? '-250' : week <= 10 ? '-500' : week <= 12 ? '-250' : '0'}`}
                            />
                          );
                        })}
                      </div>
                      <div className="flex justify-between mt-0.5">
                        <span className="text-[8px] text-slate-600">Lose</span>
                        <span className="text-[8px] text-slate-600">Maintain</span>
                      </div>
                    </>
                  )}
                </div>
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-2.5 text-center">
                  <span className="text-slate-400 text-xs">
                    {(() => {
                      const target = bmrData?.dailyTarget || goals.calories;
                      const diff = totals.calories - target;
                      if (Math.abs(diff) <= 100) return 'Balance';
                      return diff < 0 ? 'Deficit' : 'Surplus';
                    })()}
                  </span>
                  <p className={`font-bold text-sm ${(() => {
                    const target = bmrData?.dailyTarget || goals.calories;
                    const diff = totals.calories - target;
                    if (Math.abs(diff) <= 100) return 'text-green-400';
                    if (diff < -100) return 'text-blue-400';
                    return 'text-red-400';
                  })()}`}>
                    {(() => {
                      const target = bmrData?.dailyTarget || goals.calories;
                      const diff = Math.round(totals.calories - target);
                      return diff > 0 ? `+${diff}` : diff;
                    })()}
                  </p>
                </div>
              </div>
              
              {/* Total Daily Allowance Footer */}
              <div className="mt-3 pt-3 border-t border-slate-800">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-green-400 text-sm font-medium">Daily Target</span>
                    <p className="text-slate-500 text-xs">
                      TDEE {bmrData?.tdee || goals.calories}{bmrData?.calorieAdjustment ? ` ${bmrData.calorieAdjustment}` : ''} + Walk {bmrData?.walkCalories || stepsToCalories(externalSteps, bodyWeight)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-green-400 font-bold text-lg">{bmrData?.dailyTarget || (goals.calories + stepsToCalories(externalSteps, bodyWeight))} kcal</span>
                  </div>
                </div>
              </div>
              
              {/* External Steps Input */}
              <div className="mt-3 pt-3 border-t border-slate-800">
                <div className="flex items-center justify-between p-2.5 bg-slate-800/50 rounded-xl border border-slate-700/50">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-cyan-500 to-sky-600 flex items-center justify-center">
                      <Footprints className="h-3.5 w-3.5 text-white" />
                    </div>
                    <div>
                      <span className="text-xs text-white font-medium">External Steps</span>
                      <p className="text-[9px] text-slate-500">Smartwatch / App</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      value={externalSteps || ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        const newValue = val === '' ? 0 : parseInt(val) || 0;
                        updateExternalSteps(newValue);
                      }}
                      placeholder="0"
                      className="w-16 h-8 text-center bg-slate-900 border-slate-700 text-white text-xs rounded-lg"
                      data-testid="external-steps-input"
                    />
                    <span className="text-xs text-slate-400">steps</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Macros Ring Diagrams */}
          <Card className="bg-slate-900/80 border-slate-800 rounded-2xl">
            <CardContent className="p-3">
              <div className="flex items-center justify-around">
                {/* Protein Ring */}
                <div className="flex flex-col items-center">
                  <RingProgress
                    value={totals.protein}
                    max={goals.protein}
                    size={70}
                    strokeWidth={6}
                    color="#ef4444"
                    label={`${Math.round(totals.protein)}g`}
                  />
                  <span className="text-slate-500 text-[11px] mt-0.5">/ {goals.protein}g</span>
                  <span className="text-red-400 text-sm font-semibold">Protein</span>
                </div>
                
                {/* Carbs Ring */}
                <div className="flex flex-col items-center">
                  <RingProgress
                    value={totals.carbs}
                    max={goals.carbs}
                    size={70}
                    strokeWidth={6}
                    color="#eab308"
                    label={`${Math.round(totals.carbs)}g`}
                  />
                  <span className="text-slate-500 text-[11px] mt-0.5">/ {goals.carbs}g</span>
                  <span className="text-yellow-400 text-sm font-semibold">Carbs</span>
                </div>
                
                {/* Fat Ring */}
                <div className="flex flex-col items-center">
                  <RingProgress
                    value={totals.fat}
                    max={goals.fat}
                    size={70}
                    strokeWidth={6}
                    color="#3b82f6"
                    label={`${Math.round(totals.fat)}g`}
                  />
                  <span className="text-slate-500 text-[11px] mt-0.5">/ {goals.fat}g</span>
                  <span className="text-blue-400 text-sm font-semibold">Fat</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Walk Impact Card (Yesterday's feedback) */}
          <WalkImpactCard />

          {/* Action Buttons */}
          <div data-testid="buttons-grid">
            <ActionButtons onAction={handleAction} isExpired={isExpired} />
          </div>

          {/* Food Entry List */}
          <div data-testid="meal-times">
            <FoodEntryList
              entries={entries}
              mealTimes={allMealTimes}
              selectedMealTime={selectedMealTime}
              onSelectMealTime={setSelectedMealTime}
              onDeleteEntry={removeEntry}
              onCopyEntry={handleCopyEntry}
              onVoiceFood={() => voiceFood.isRecording ? voiceFood.stopRecording() : voiceFood.startRecording()}
              voiceFoodRecording={voiceFood.isRecording}
              voiceFoodProcessing={voiceFood.isProcessing}
              voiceLanguage={voiceFood.language}
              onVoiceLanguageChange={(lang) => voiceFood.setLanguage(lang)}
              voiceLanguages={voiceFood.languages}
              onCopyDay={() => {
                // Copy all entries to clipboard / another day
                if (entries && entries.length > 0) {
                  setShowCopyFood(true);
                  setCopyEntry({ ...entries[0], _copyAll: true, _allEntries: entries });
                } else {
                  toast.info('No food entries to copy');
                }
              }}
              onSaveToMeals={() => {
                if (entries && entries.length > 0) {
                  const dateObj = new Date(selectedDate + 'T12:00:00');
                  const mealName = `Day ${dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
                  saveMeal(mealName, entries);
                } else {
                  toast.info('No food entries to save');
                }
              }}
              onCopyMeal={(mealTime, mealEntries) => {
                if (mealEntries.length > 0) {
                  setShowCopyFood(true);
                  setCopyEntry({ ...mealEntries[0], _copyAll: true, _allEntries: mealEntries, _mealName: mealTime.name });
                } else {
                  toast.info(`No items in ${mealTime.name} to copy`);
                }
              }}
              onSaveMeal={(mealTime, mealEntries) => {
                if (mealEntries.length > 0) {
                  saveMeal(mealTime.name, mealEntries);
                } else {
                  toast.info(`No items in ${mealTime.name} to save`);
                }
              }}
              bloodSugarByMeal={bloodSugarByMeal}
              onBloodSugarChange={handleBloodSugarChange}
              diabetesEnabled={diabetesEnabled}
              diabetesUnit={diabetesUnit}
              insulinEnabled={insulinEnabled}
            />
          </div>
        </div>
      </main>

      {/* Modals */}
      <EditGoalsModal
        isOpen={showEditGoals}
        onClose={() => setShowEditGoals(false)}
        goals={goals}
        onSave={(newGoals) => {
          setGoals(newGoals);
          localStorage.setItem('gym_notebook_nutrition_goals', JSON.stringify(newGoals));
          toast.success('Goals updated!');
        }}
      />

      <FoodSearchModal
        isOpen={showFoodSearch}
        onClose={() => setShowFoodSearch(false)}
        onAddFood={handleAddFood}
        onOpenManualEntry={() => {
          setShowFoodSearch(false);
          setShowManualEntry(true);
        }}
        mealTimes={allMealTimes}
      />

      <BarcodeScannerModal
        isOpen={showBarcodeScanner}
        onClose={() => setShowBarcodeScanner(false)}
        onAddFood={handleAddFood}
        savedBarcodes={savedBarcodes}
        onSaveBarcode={saveBarcode}
        mealTimes={allMealTimes}
      />

      <SavedBarcodesModal
        isOpen={showSavedBarcodes}
        onClose={() => setShowSavedBarcodes(false)}
        savedBarcodes={savedBarcodes}
        onAddFood={handleAddFood}
        mealTimes={allMealTimes}
        onDeleteBarcode={(barcode) => {
          deleteBarcode(barcode);
          toast.success('Product removed');
        }}
        onUpdateBarcode={(barcode, updates) => {
          updateBarcode(barcode, updates);
        }}
      />

      <SavedMealsModal
        isOpen={showSavedMeals}
        onClose={() => setShowSavedMeals(false)}
        savedMeals={savedMeals}
        onAddMeal={(meal) => {
          const items = meal.items || meal.entries || [];
          const targetMealTime = meal.mealTime || selectedMealTime;
          items.forEach(item => handleAddFood({ ...item, mealTime: targetMealTime }));
          toast.success(`Added ${items.length} items to ${targetMealTime}`);
          setShowSavedMeals(false);
        }}
        onDeleteMeal={deleteMeal}
        onRenameMeal={(mealId, newName) => {
          renameMeal(mealId, newName);
        }}
        mealTimes={allMealTimes}
      />

      <FreshProduceModal
        isOpen={showFreshProduce}
        onClose={() => setShowFreshProduce(false)}
        onAddFood={handleAddFood}
        mealTimes={allMealTimes}
      />

      <CustomProductsModal
        isOpen={showCustomProducts}
        onClose={() => setShowCustomProducts(false)}
        customProducts={customProducts}
        onAddFood={handleAddFood}
        onSaveProduct={saveCustomProduct}
        onDeleteProduct={(id) => {
          // Custom products are stored in localStorage - need to implement deletion
          const updated = customProducts.filter(p => p.id !== id);
          localStorage.setItem('gym_notebook_custom_products', JSON.stringify(updated));
          toast.success('Product deleted');
        }}
        onEditProduct={(id, product) => {
          // Edit existing product
          saveCustomProduct({ ...product, id });
        }}
        mealTimes={allMealTimes}
      />

      <ManualEntryModal
        isOpen={showManualEntry}
        onClose={() => setShowManualEntry(false)}
        onAddFood={handleAddFood}
        mealTimes={allMealTimes}
      />

      <FoodPhotoModal
        isOpen={showFoodPhoto}
        onClose={() => setShowFoodPhoto(false)}
        onAddFood={handleAddFood}
        mealTimes={allMealTimes}
      />

      <CopyFoodModal
        isOpen={showCopyFood}
        onClose={() => {
          setShowCopyFood(false);
          setCopyEntry(null);
        }}
        entry={copyEntry}
        mealTimes={allMealTimes}
        onCopy={handleCopyToDate}
      />

      <AddMealTimeModal
        isOpen={showAddMealTime}
        onClose={() => setShowAddMealTime(false)}
        onAdd={(mealTime) => {
          addCustomMealTime(mealTime.name);
        }}
      />

      <MenuScannerModal
        isOpen={showMenuScanner}
        onClose={() => setShowMenuScanner(false)}
        onAddFood={handleAddFood}
        mealTimes={allMealTimes}
        selectedMealTime={selectedMealTime}
        onMealTimeChange={setSelectedMealTime}
        savedMenus={savedMenus}
      />
    </div>
  );
}

// Main component that wraps with context
export default function NutritionTrackerRefactored({ onBack, isExpired = false, customMealTimes, onNavigate }) {
  return (
    <NutritionProvider isExpired={isExpired} customMealTimes={customMealTimes}>
      <NutritionTrackerInner onBack={onBack} isExpired={isExpired} onNavigate={onNavigate} />
    </NutritionProvider>
  );
}
